// Person identity capture, shared by every surface that can create a person: the People
// page, the household member editor, and step 1 of the create-benefit flow. A household
// member is a real person record, so all three have to apply the same duplicate rule.

export interface PersonIdentityDraft {
  firstName: string
  lastName: string
  middleName: string
  socialSecurityNumber: string
  dateOfBirth: string
  // The select value, kept as a string so it round-trips through the shadcn Select.
  gender: string
}

export const emptyPersonIdentity: PersonIdentityDraft = {
  firstName: "",
  lastName: "",
  middleName: "",
  socialSecurityNumber: "",
  dateOfBirth: "",
  gender: "",
}

export interface PersonNameParts {
  firstName?: string | null
  lastName?: string | null
  middleName?: string | null
}

/** Staff-facing person names use the same Last Name, First Name order everywhere. */
export function formatPersonName(person: PersonNameParts): string {
  const lastName = person.lastName?.trim() ?? ""
  const givenNames = [person.firstName?.trim(), person.middleName?.trim()].filter(Boolean).join(" ")
  if (lastName && givenNames) return `${lastName}, ${givenNames}`
  return lastName || givenNames || "—"
}

export const genderOptions = [
  { value: "0", label: "Female" },
  { value: "1", label: "Male" },
  { value: "2", label: "Trans-Male" },
  { value: "3", label: "Trans-Female" },
  { value: "4", label: "Gender Non-Conforming" },
  { value: "5", label: "Unknown" },
]

// The duplicate-detection endpoint parses Gender from the value-type member name, not the
// byte, so the query string spells the member out.
export const genderQueryName: Record<string, string> = {
  "0": "Female",
  "1": "Male",
  "2": "Transmale",
  "3": "Transfemale",
  "4": "GenderNonConforming",
  "5": "Unknown",
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "")
}

/**
 * The backend only returns duplicate matches once name, gender, birth date and a complete
 * SSN are all present, so there is nothing to ask it before that.
 */
export function canCheckDuplicates(draft: PersonIdentityDraft): boolean {
  return (
    draft.firstName.trim() !== "" &&
    draft.lastName.trim() !== "" &&
    draft.dateOfBirth !== "" &&
    draft.gender !== "" &&
    digitsOnly(draft.socialSecurityNumber).length === 9
  )
}

export function duplicateQuery(draft: PersonIdentityDraft): string {
  return new URLSearchParams({
    LastName: draft.lastName.trim(),
    FirstName: draft.firstName.trim(),
    BirthDate: draft.dateOfBirth,
    Gender: genderQueryName[draft.gender] ?? "",
    SocialSecurityNumber: digitsOnly(draft.socialSecurityNumber),
  }).toString()
}

/** Returns the message to show the user, or null when the draft can be submitted. */
export function validatePersonIdentity(draft: PersonIdentityDraft): string | null {
  if (
    !draft.firstName.trim() ||
    !draft.lastName.trim() ||
    !draft.dateOfBirth ||
    !draft.gender
  ) {
    return "Last name, first name, date of birth, and gender are required."
  }

  const ssn = draft.socialSecurityNumber.trim()
  if (ssn && digitsOnly(ssn).length !== 9) {
    return "Enter a complete 9-digit Social Security Number, or leave it blank."
  }

  return null
}

/** Body for `POST /api/people`. */
export function createPersonPayload(draft: PersonIdentityDraft) {
  return {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    middleName: draft.middleName.trim() || null,
    socialSecurityNumber: draft.socialSecurityNumber.trim(),
    gender: Number.parseInt(draft.gender, 10),
    birthDate: draft.dateOfBirth,
  }
}

/** The `newPerson` block of `POST /api/benefitapplications/{id}/household-members`. */
export function newHouseholdMemberPerson(draft: PersonIdentityDraft) {
  const ssn = digitsOnly(draft.socialSecurityNumber)
  return {
    lastName: draft.lastName.trim(),
    firstName: draft.firstName.trim(),
    middleName: draft.middleName.trim(),
    socialSecurityNumber: ssn.length === 9 ? ssn : null,
    birthDate: draft.dateOfBirth,
    gender: Number.parseInt(draft.gender, 10),
  }
}

/** A match returned by `/api/people/search` and `/api/people/potential-duplicates`. */
export interface PotentialPersonMatch {
  id: string
  lastName: string
  firstName: string
  socialSecurityNumber: string | null
  dateOfBirth: string | null
  gender: number
  confidence?: number
  liheapEligibility?: number
}

export interface PersonSearchResult {
  matches: PotentialPersonMatch[]
  hasMoreMatches: boolean
}

/** Full identifier display used only by the staff-authorized Find Person surface. */
export function displaySsn(ssn: string | null | undefined): string {
  if (!ssn) return "—"
  const digits = digitsOnly(ssn)
  return digits.length === 9 ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}` : ssn
}

/** Masked display retained for non-search contexts that should not repeat full identifiers. */
export function maskSsn(ssn: string | null | undefined): string {
  if (!ssn) return "—"
  const digits = digitsOnly(ssn)
  return digits.length === 9 ? `***-**-${digits.slice(5)}` : ssn
}
